<script setup lang="ts">
import {
  InfoCircle,
  SearchMd,
  File06,
  Download01,
  Trash01,
  FilePlus02,
  Plus,
} from "utruckin-icons";
import CommonSearch from "@/components/common/CommonSearch.vue";
import CommonDropdown from "@/components/common/CommonDropdown.vue";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { ITeam } from "@/features/teams/team.types";
import { useTeamStore } from "@/features/teams/team.store";
import { useInspectionStore } from "@/features/inspections/inspection.store";
import {
  IInspectionItem,
  IManualInspection,
} from "@/features/inspections/inspection.types";
import CommonTable from "@/components/common/CommonTable.vue";
import { DateTime } from "luxon";
import CommonButton from "@/components/common/CommonButton.vue";
import CommonModal from "@/components/common/CommonModal.vue";
import CommonInput from "@/components/common/CommonInput.vue";
import { useNotification } from "@/utils/notification";
import { useUserStore } from "@/features/users/user.store";
import CustomTab from "@/components/custom/CustomTab.vue";
import { ICompany } from "@/features/company/company.types";
import { useCompanyStore } from "@/features/company/company.store";
import { IDriver } from "@/features/drivers/drivers.types";
import { useDriverStore } from "@/features/drivers/drivers.store";
import CustomTableDropdown from "@/components/custom/CustomTableDropdown.vue";
import "@vuepic/vue-datepicker/dist/main.css";
import { VueDatePicker } from "@vuepic/vue-datepicker";
import { useAppStore } from "@/store/app.store";

const teamStore = useTeamStore();
const companyStore = useCompanyStore();
const inspectionStore = useInspectionStore();
const userStore = useUserStore();
const driverStore = useDriverStore();
const appStore = useAppStore();

const { pushNotification } = useNotification();

const inspections = ref<IInspectionItem[]>([]);
const inspectionLoading = ref(false);
const hasMore = ref(true);
const loadMoreTrigger = ref<HTMLElement | null>(null);
const bodyRef = ref<HTMLElement | null>(null);
const teams = ref<ITeam[]>([]);
const teamLoading = ref(false);
const companies = ref<ICompany[]>([]);
const companyLoading = ref(false);
const drivers = ref<IDriver[]>([]);
const driverLoading = ref(false);
const team = ref<string | null>(null);
const search = ref<string>("");
const modalVisible = ref<boolean>(false);
const modalItem = ref<IInspectionItem>();
const modalLoading = ref(false);
const fileInput = ref();
const file = ref<any>(null);
const comment = ref<string | boolean>("");

const addModal = ref<boolean>(false);
const addModalItem = ref<IManualInspection>({
  company_id: "",
  company_name: "",
  driver_id: "",
  driver_name: "",
  filecode: "",
  type: "",
  status: "",
  start_date: "",
  end_date: "",
  processed_time: "",
  comment: "",
});
const addModalLoading = ref<boolean>(false);

let searchCompanyTimer: ReturnType<typeof setTimeout> | null = null;
let searchDriverTimer: ReturnType<typeof setTimeout> | null = null;

const activeTab = ref<number>(0);
const tabs = [
  { label: "All", value: 0 },
  { label: "With file", value: 1 },
  { label: "No file", value: 2 },
];

let searchTimer: ReturnType<typeof setTimeout> | null = null;
let observer: IntersectionObserver | null = null;

const teamItems = computed(() =>
  teams.value.map((t) => ({ value: t.id, name: t.name }))
);

const saveIsDisabled = computed(() => {
  return (file.value && file.value.name && file.value.size) || comment.value;
});

const addSaveDisabled = computed(() => {
  return (
    addModalItem.value?.company_id &&
    addModalItem.value.driver_id &&
    addModalItem.value.start_date &&
    addModalItem.value.end_date &&
    addModalItem.value.processed_time
  );
});

const isAdmin = computed(() => {
  return (
    userStore.userProfile?.role == "admin" ||
    userStore.userProfile?.role == "super_admin" ||
    userStore.userProfile?.role == "supervisor"
  );
});

const companyItems = computed(() =>
  (companies.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
    app: c.provider_name,
  }))
);

const driverItems = computed(() =>
  (drivers.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
    app: c.truck_number,
  }))
);

function datepickerToUtcDateOnly(d: any) {
  return d
    ? DateTime.fromISO(d, {
        zone: "utc",
      })
        .setZone("America/New_York")
        .toFormat("MMM d, HH:mm")
    : "-";
}

function datepickerToUtcDateOnlyWithoutTime(d: any) {
  return DateTime.fromISO(d, {
    zone: "utc",
  })
    .setZone("America/New_York")
    .toFormat("MMM d");
}

const getList = async () => {
  const [fetchedTeams, fetchedCompanies] = await Promise.all([
    teamStore.getTeams(teamLoading),
    companyStore.getSimpleCompanies(companyLoading),
  ]);

  teams.value = fetchedTeams;
  companies.value = fetchedCompanies.items;

  if (inspectionLoading.value || !hasMore.value) return;

  const res = await inspectionStore.getInspections(inspectionLoading);

  inspections.value.push(...res.items);

  const meta = res.meta;
  hasMore.value = meta?.page * meta.per_page < meta.total;

  inspectionStore.commonFilter.page =
    (inspectionStore.commonFilter.page as number) + 1;
};

const reload = async () => {
  inspections.value = [];
  hasMore.value = true;
  inspectionStore.commonFilter.page = 1;

  await getList();
};

const openModal = (item: IInspectionItem) => {
  modalItem.value = item;
  if (item.report_uploaded) {
    file.value = {};
  }
  comment.value = item.comment;
  modalVisible.value = true;
};

const addInspection = () => {
  addModal.value = true;
  addModalItem.value = {
    company_id: "",
    company_name: "",
    driver_id: "",
    driver_name: "",
    filecode: "",
    type: "",
    status: "",
    start_date: "",
    end_date: "",
    processed_time: "",
    comment: "",
  };
};

const closeAddModal = () => {
  addModal.value = false;
  addModalItem.value = {
    company_id: "",
    company_name: "",
    driver_id: "",
    driver_name: "",
    filecode: "",
    type: "",
    status: "",
    start_date: "",
    end_date: "",
    processed_time: "",
    comment: "",
  };
};

const saveAddInspection = () => {
  if (!addSaveDisabled.value) return;

  if (addModalItem.value) {
    if (addModalItem.value.company_name) {
      addModalItem.value.company_id = "";
      delete addModalItem.value.company_id;
    } else {
      delete addModalItem.value.company_name;
    }

    if (addModalItem.value.driver_name) {
      addModalItem.value.driver_id = "";
      delete addModalItem.value.driver_id;
    } else {
      delete addModalItem.value.driver_name;
    }

    inspectionStore.createManualInspection(
      addModalLoading,
      addModalItem.value,
      async (data: any) => {
        if (data) {
          pushNotification("Successfully created", "success");
          closeAddModal();
          await getList();
        }
      }
    );
  }
};

const closeModal = () => {
  modalVisible.value = false;
  file.value = null;
  comment.value = "";
};

const handleFile = (e: any) => {
  file.value = e.target.files?.[0] ?? null;
};

const handleDrop = (event: any) => {
  const files = event.dataTransfer?.files;

  if (!files?.length) return;

  const newFile = files[0];

  file.value = newFile ?? null;
};

const downloadFile = async () => {
  const data = await inspectionStore.downloadReport(
    modalLoading,
    modalItem.value?.id as string
  );
  if (data) {
    const fileUrl = window.URL.createObjectURL(data);
    const link = document.createElement("a");
    link.href = fileUrl;
    link.setAttribute("download", modalItem.value?.report_filename as string);

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(fileUrl);
  }
};

const searchCompany = (val: string) => {
  if (searchCompanyTimer) clearTimeout(searchCompanyTimer);
  searchCompanyTimer = setTimeout(async () => {
    companyStore.commonSimpleFilter.search = val;
    companies.value = (
      await companyStore.getSimpleCompanies(companyLoading)
    ).items;
  }, 400);
};

const searchDriver = (val: string) => {
  if (addModalItem.value.company_name) return;
  if (searchDriverTimer) clearTimeout(searchDriverTimer);
  searchDriverTimer = setTimeout(async () => {
    driverStore.commonSimpleFilter.search = val;
    const [fetchedDrivers] = await Promise.all([
      driverStore.getSimpleDrivers(driverLoading),
    ]);

    drivers.value = fetchedDrivers.items;
  }, 400);
};

const deleteFile = async () => {
  const data = await inspectionStore.deleteReport(
    modalLoading,
    modalItem.value?.id as string
  );

  if (data) {
    pushNotification("Successfully uploaded", "success");
    closeModal();
    await reload();
  }
};

const save = async () => {
  let data;
  let commentData;
  if (file.value && file.value.name) {
    data = await inspectionStore.uploadReport(
      modalLoading,
      modalItem.value?.id as string,
      file.value as File
    );
  }

  if (comment.value) {
    commentData = await inspectionStore.updateComment(
      modalLoading,
      modalItem.value?.id as string,
      { text: comment.value }
    );
  }

  if (data) {
    pushNotification("Successfully uploaded", "success");
  }

  if (commentData) {
    pushNotification("Successfully updated", "success");
  }

  closeModal();
  await reload();
};

onMounted(async () => {
  if (userStore.userProfile && userStore.userProfile.team_id) {
    team.value = userStore.userProfile.team_id;
    inspectionStore.commonFilter.team_id = userStore.userProfile.team_id;
  }

  inspections.value = [];
  hasMore.value = true;
  inspectionStore.commonFilter.page = 1;

  await getList();

  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        getList();
      }
    },
    {
      root: bodyRef.value,
      rootMargin: "200px",
      threshold: 0,
    }
  );

  if (loadMoreTrigger.value) {
    observer.observe(loadMoreTrigger.value);
  }
});

onUnmounted(() => {
  observer?.disconnect();
});

watch(search, (val) => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(async () => {
    inspectionStore.commonFilter.search = val;
    await reload();
  }, 400);
});

watch(team, async (val) => {
  inspectionStore.commonFilter.team_id = val as string;
  await reload();
});

watch(
  () => activeTab.value,
  async (newVal) => {
    if (inspectionStore.commonFilter) {
      if (newVal == 0) {
        inspectionStore.commonFilter.report_uploaded = null;
      } else inspectionStore.commonFilter.report_uploaded = newVal == 1;

      await reload();
    }
  }
);

watch(
  () => addModalItem.value?.company_id,
  async (val) => {
    const newVal = companies.value.some(
      (item) => item.id.toLowerCase() === val
    );

    drivers.value = [];
    addModalItem.value.driver_id = "";

    if (!newVal) {
      addModalItem.value.company_name = val;
      driverStore.commonSimpleFilter.company_id = "";
      drivers.value = [];
    } else {
      if (val) {
        driverStore.commonSimpleFilter.company_id = val;
        const [fetchedDrivers] = await Promise.all([
          driverStore.getSimpleDrivers(driverLoading),
        ]);

        drivers.value = fetchedDrivers.items;
      }
    }
  }
);

watch(
  () => addModalItem.value.driver_id,
  (val) => {
    const newVal = drivers.value.some((item) => item.id.toLowerCase() === val);

    if (!newVal) {
      addModalItem.value.driver_name = val;
    }
  }
);

watch(
  () => appStore.refreshKey,
  () => {
    getList();
  },
  {
    immediate: false,
  }
);
</script>

<template>
  <div class="v-inspections">
    <div class="header">
      <CommonSearch
        v-model="search"
        :icon="SearchMd"
        placeholder="Search"
        clearable
      />
      <CommonDropdown
        v-model="team"
        :items="teamItems"
        label="Team"
        clearable
      />

      <CustomTab :tabs="tabs" v-model="activeTab" />

      <CommonButton
        type="primary"
        label="Add Inspection"
        :prepend="true"
        :prepend-icon="Plus"
        @click="addInspection"
      />
    </div>
    <div class="body" ref="bodyRef">
      <CommonTable
        :headers="inspectionStore.inspectionsHeaders"
        :loading="inspectionLoading"
        :items="inspections"
      >
        <template #id="{ index }">
          <span>{{ index + 1 }}</span>
        </template>

        <template #last_driver_task_completed_time="{ item }">
          {{
            datepickerToUtcDateOnly(item.last_driver_task_completed_time) || "-"
          }}
        </template>

        <template #created_time="{ item }">
          {{ datepickerToUtcDateOnly(item.created_time) }}
        </template>

        <template #last_driver_task_user_name="{ item }">
          {{ item.last_driver_task_user_name || "-" }}
        </template>

        <template #period="{ item }">
          {{
            datepickerToUtcDateOnlyWithoutTime(item.start_date) +
            " - " +
            datepickerToUtcDateOnlyWithoutTime(item.end_date)
          }}
        </template>

        <template #processed_time="{ item }">
          {{ datepickerToUtcDateOnly(item.processed_time) }}
        </template>

        <template #log_violations_during_inspection_count="{ item }">
          <div style="text-align: center">
            {{ item.log_violations_during_inspection_count }}
          </div>
        </template>

        <template #has_eld_history_changes_after_last_task="{ item }">
          {{ item.has_eld_history_changes_after_last_task ? "Yes" : "No" }}
        </template>

        <template #type="{ item }">
          {{ item.type == "manual" ? "Manual" : "Auto" }}
        </template>

        <template #actions="{ item }">
          <div
            class="action"
            :class="item.report_uploaded ? 'uploaded' : ''"
            @click="openModal(item)"
          >
            <InfoCircle :size="18" />
          </div>
        </template>
      </CommonTable>

      <div ref="loadMoreTrigger" />
    </div>

    <CommonModal v-model="modalVisible" title="Add file" @close="closeModal">
      <div class="modal__body">
        <table>
          <thead>
            <tr>
              <th>Company</th>
              <th>Driver</th>
              <th>Date</th>
              <th>File</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ modalItem?.company_name }}</td>
              <td>{{ modalItem?.driver_name }}</td>
              <td>
                {{ datepickerToUtcDateOnly(modalItem?.created_time as string) }}
              </td>
              <td>{{ modalItem?.report_uploaded ? "1" : "0" }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="modalItem?.report_uploaded" class="modal__body--file">
          <div class="icon">
            <File06 :size="20" />
          </div>
          <div class="name">
            {{ modalItem?.report_filename }}
          </div>
          <div class="action">
            <div class="action__download" @click="downloadFile">
              <Download01 :size="16" />
            </div>
            <div v-if="isAdmin" class="action__delete" @click="deleteFile">
              <Trash01 :size="16" />
            </div>
          </div>
        </div>

        <div v-if="file && file.size && file.name" class="modal__body--file">
          <div class="icon">
            <File06 :size="20" />
          </div>
          <div class="file">
            <div class="file__name">
              {{ file.name }}
            </div>
            <div class="file__size">
              {{ (file.size / 1024 / 1024).toFixed(2) }} MB
            </div>
          </div>
          <div class="action">
            <div class="action__delete" @click="file = null">
              <Trash01 :size="16" />
            </div>
          </div>
        </div>

        <div
          class="modal__body--comment"
          :class="modalItem?.report_uploaded ? '' : 'bottom'"
        >
          <CommonInput v-model="comment" label="Comment" multiline />
        </div>

        <div class="modal__body--input" v-if="!file">
          <input
            ref="fileInput"
            type="file"
            hidden
            @change="handleFile"
            accept="application/pdf, image/jpeg, image/jpg, image/png"
          />

          <div
            class="upload-zone"
            @click="$refs.fileInput.click()"
            @dragover.prevent
            @drop.prevent="handleDrop"
          >
            <FilePlus02 :size="16" />
            Add file
          </div>
        </div>
      </div>

      <template #footer>
        <div
          style="
            display: flex;
            justify-content: flex-end;
            width: 100%;
            gap: 16px;
          "
        >
          <CommonButton
            :is-disabled="!saveIsDisabled"
            type="primary"
            label="Save"
            @clicked="save"
          />
          <CommonButton type="outline" label="Back" @clicked="closeModal" />
        </div>
      </template>
    </CommonModal>

    <CommonModal
      v-model="addModal"
      title="Add Inspection"
      @close="closeAddModal"
    >
      <div v-if="addModalItem">
        <div class="dropdown">
          <CustomTableDropdown
            v-model="addModalItem.company_id"
            :items="companyItems"
            label="Company"
            position="bottom"
            clearable
            fullWidth
            isAppendItem
            dropdownFullWidth
            @search="searchCompany"
            creatable
          />
        </div>

        <div class="dropdown">
          <CustomTableDropdown
            v-model="addModalItem.driver_id"
            :items="driverItems"
            label="Driver"
            fullWidth
            clearable
            position="bottom"
            isAppendItem
            creatable
            @search="searchDriver"
          />
        </div>

        <div style="margin-bottom: 16px">
          <!--          <CommonInput-->
          <!--            mask="####-##-##"-->
          <!--            v-model="addModalItem.start_date"-->
          <!--            label="Start date (YYYY-MM-DD)"-->
          <!--          />-->

          <VueDatePicker
            placeholder="Start date"
            class="time"
            model-type="iso"
            v-model="addModalItem.start_date"
          ></VueDatePicker>
        </div>
        <div style="margin-bottom: 16px">
          <VueDatePicker
            placeholder="Processed date"
            class="time"
            model-type="iso"
            v-model="addModalItem.processed_time"
          ></VueDatePicker>
        </div>
        <div style="margin-bottom: 16px">
          <VueDatePicker
            placeholder="End date"
            class="time"
            model-type="iso"
            v-model="addModalItem.end_date"
          ></VueDatePicker>
        </div>

        <div class="comment">
          <CommonInput
            v-model="addModalItem.comment"
            label="Comment"
            multiline
          />
        </div>
      </div>

      <template #footer>
        <div
          style="
            display: flex;
            justify-content: flex-end;
            width: 100%;
            gap: 16px;
          "
        >
          <CommonButton
            :is-disabled="!addSaveDisabled"
            type="primary"
            label="Save"
            @clicked="saveAddInspection"
          />
          <CommonButton type="outline" label="Back" @clicked="closeAddModal" />
        </div>
      </template>
    </CommonModal>
  </div>
</template>

<style scoped lang="scss">
.v-inspections {
  background: var(--color-system-background);

  .header {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 16px;
  }

  .body {
    height: calc(100vh - 156px);
    padding: 0 16px;
    overflow-y: auto;

    .action {
      width: 32px;
      height: 32px;
      border: 1px solid var(--color-border);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--color-system-red);
      background: var(--color-secondary-system-background);

      &.uploaded {
        color: var(--color-system-green);
      }
    }
  }
}

.modal__body {
  table {
    width: 100%;
    border-spacing: 0;
    border-collapse: separate;

    th,
    td {
      border: 1px solid var(--color-border);
      padding: 12px 16px;
    }

    th {
      color: var(--color-label-secondary);
    }

    td {
      color: var(--color-label);
    }

    th:first-child {
      border-top-left-radius: 14px;
    }

    th:last-child {
      border-top-right-radius: 14px;
    }

    td:first-child {
      border-bottom-left-radius: 14px;
    }

    td:last-child {
      border-bottom-right-radius: 14px;
    }
  }

  &--file {
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--color-secondary-system-background);
    margin-top: 8px;
    border-radius: 14px;
    border: 1px solid var(--color-border);
    padding: 12px;
    color: var(--color-label);

    .name {
      width: 100%;
    }

    .file {
      width: 100%;
    }

    .action {
      display: flex;
      align-items: center;
      gap: 8px;

      &__download,
      &__delete {
        width: 32px;
        height: 32px;
        border: 1px solid var(--color-border);
        border-radius: 12px;
        background: var(--color-system-background);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }

      &__delete {
        color: var(--color-system-red);
      }
    }
  }

  &--comment {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid var(--color-border);

    &.bottom {
      padding-bottom: 16px;
      border-bottom: 1px solid var(--color-border);
    }
  }

  &--input {
    display: block;
    width: 100%;
    cursor: pointer;
    padding-top: 16px;

    .upload-zone {
      height: 110px;
      border: 1px dashed #8b96ac;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      color: var(--color-label-secondary);
      font-size: 14px;
      line-height: 20px;
      transition: 0.2s;
    }
  }
}

.dropdown {
  width: 100%;
  border: 1px solid var(--color-border);
  border-radius: 14px;
  background: var(--color-secondary-system-background);
  margin-bottom: 16px;
}

.comment {
  padding-top: 16px;
  border-top: 1px solid var(--color-border);
}

.time {
  --dp-background-color: var(--color-system-background);
  --dp-text-color: var(--color-label);
  --dp-border-color: var(--color-border);
}
</style>
